import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { I18NextPipe } from 'angular-i18next';
import { MenuItem } from 'primeng/api';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Role } from 'src/app/backend-client/models/Backend/Core/Infrastructure/Aggregates/Identity/role';
import { MxAppInitService, MxBreadcrumbService, MxIdentityService, MxMenuService } from '../../services';
import { PortalNotificationDto, PortalNotificationKind, SzNotificationService } from '../../services/sz-notification.service';
import { SzHelpService } from '../../services/sz-help.service';
import { SzIssueReportDialogService } from '../../services/sz-issue-report-dialog.service';
import { SzShellService } from '../../services/sz-shell.service';

/**
 * Felső sáv a prototípus szerint: breadcrumb (Pharmaroad B2B › modul), oldalcím, környezet-címke,
 * globális kereső (Ctrl K), értesítés-harang számlálóval; 1024 px alatt hamburger a fiókhoz.
 */
@Component({
  standalone: false,
  selector: 'sz-topbar',
  templateUrl: './sz-topbar.component.html',
  styleUrls: ['./sz-topbar.component.scss'],
})
export class SzTopbarComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  pageTitle = '';
  crumb = '';
  crumbRoot = 'Pharmaroad B2B';
  envLabel = '';
  badge = 0;
  narrow = false;
  searchTerm = '';
  /** Értesítés-panel nyitva. */
  notifOpen = false;
  /** A „?” menüje nyitva (Súgó / Hiba bejelentése, csak patikai szerepkörnél). */
  helpOpen = false;
  notifications: PortalNotificationDto[] = [];
  private subs = new Subscription();

  constructor(
    private breadcrumbService: MxBreadcrumbService,
    private menuService: MxMenuService,
    private router: Router,
    private appInitService: MxAppInitService,
    private i18NextPipe: I18NextPipe,
    public shell: SzShellService,
    private notificationService: SzNotificationService,
    private helpService: SzHelpService,
    private identityService: MxIdentityService,
    private issueReportDialog: SzIssueReportDialogService
  ) {}

  /** A kereső a Számláim listában keres, ezért csak a számlát látó (patikai) szerepköröknek jelenik meg. */
  get canSearch(): boolean {
    const role = Number(this.identityService.accountValue?.role);
    return [Role.Pharmacy, Role.PartnerProgramPharmacy, Role.PharmacyBasic].includes(role);
  }

  /** Van-e a szerepkörnek súgója (patika vagy admin). */
  get hasHelp(): boolean {
    return !!this.helpService.audience;
  }

  /** Van-e a „?” alatt menü: a patikák hibát is jelenthetnek, az admin csak a súgót kapja. */
  get canReportIssue(): boolean {
    return this.issueReportDialog.canReport;
  }

  /** „?”: patikának menü (Súgó / Hiba bejelentése), adminnak közvetlenül a súgó. */
  onHelpClick() {
    this.notifOpen = false;
    if (this.canReportIssue) {
      this.helpOpen = !this.helpOpen;
    } else {
      this.openHelp();
    }
  }

  /** Súgó az aktuális oldal fejezeténél. */
  openHelp() {
    this.notifOpen = false;
    this.helpOpen = false;
    this.helpService.openForCurrentPage();
  }

  /** Hiba bejelentése az aktuális útvonallal előre kitöltve. */
  reportIssue() {
    this.helpOpen = false;
    this.issueReportDialog.open({ page: this.router.url });
  }

  ngOnInit(): void {
    this.onResize();
    this.envLabel = this.resolveEnvLabel();
    this.subs.add(this.breadcrumbService.itemsHandler.subscribe((items) => this.applyBreadcrumb(items)));
    this.subs.add(this.shell.badge$.subscribe((b) => (this.badge = b)));
    this.subs.add(this.notificationService.items$.subscribe((items) => (this.notifications = items)));
    this.subs.add(this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => this.applyCrumbFromMenu()));
    this.applyCrumbFromMenu();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  @HostListener('window:resize')
  onResize() {
    this.narrow = window.innerWidth < 1024;
  }

  /** Kattintás a panelen kívül: bezár. */
  @HostListener('document:click')
  onDocumentClick() {
    this.notifOpen = false;
    this.helpOpen = false;
  }

  toggleNotifications() {
    this.helpOpen = false;
    this.notifOpen = !this.notifOpen;
    if (this.notifOpen) {
      this.notificationService.refresh().subscribe();
    }
  }

  openNotification(n: PortalNotificationDto) {
    if (!n.readAt) {
      this.notificationService.markRead([n.id]).subscribe();
    }
    this.notifOpen = false;
    if (n.link) {
      this.router.navigateByUrl(n.link);
    }
  }

  markAllRead() {
    this.notificationService.markAllRead().subscribe();
  }

  iconOf(kind: PortalNotificationKind): string {
    return SzNotificationService.icon(kind);
  }

  trackNotif = (_: number, n: PortalNotificationDto) => n.id;

  /** „5 perce”, „2 órája”, „3 napja”, különben dátum. */
  ago(iso: string): string {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'most';
    if (min < 60) return `${min} perce`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h} órája`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d} napja`;
    const dt = new Date(iso);
    return `${dt.getFullYear()}. ${String(dt.getMonth() + 1).padStart(2, '0')}. ${String(dt.getDate()).padStart(2, '0')}.`;
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.notifOpen = false;
      this.helpOpen = false;
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k' && this.searchInput) {
      event.preventDefault();
      this.searchInput?.nativeElement.focus();
    }
  }

  /** Kezdőlapon mobilon a logó jelenik meg a cím helyett (prototípus M10). */
  get isHome(): boolean {
    const p = (this.router.url || "").split("?")[0];
    return p === "/" || p === "";
  }

  /** Enter: a Számláim lista nyílik a kifejezéssel mint táblázat-kereséssel (?q=). */
  submitSearch() {
    const q = (this.searchTerm || '').trim();
    if (!q || !this.canSearch) return;
    this.shell.search(q);
    this.router.navigate(['/invoice'], { queryParams: { q } });
  }

  private applyBreadcrumb(items: MenuItem[]) {
    const labels = (items || []).map((i) => i.label).filter((l) => !!l);
    if (labels.length === 0) {
      return;
    }
    // A komponens által beállított többtagú breadcrumb (pl. Reklamációk › Új reklamáció) címe és modulja nyer.
    this.pageTitle = labels[labels.length - 1];
    if (labels.length > 1) {
      this.crumb = labels[0];
    } else if (!this.crumb) {
      this.crumb = this.pageTitle;
    }
  }

  /** A modul neve a menüből: ha az aktív útvonal egy csoport alatt van, a csoport címe; különben az oldal címe. */
  private applyCrumbFromMenu() {
    const url = this.router.url.split(/[?#]/)[0];
    let crumb = '';
    let title = '';
    for (const m of (this.menuService.menu || []).filter((x: any) => x.visible !== false)) {
      if (m.items) {
        const hit = m.items.find((c: any) => c.visible !== false && c.routerLink && this.matches(url, c.routerLink));
        if (hit) {
          crumb = this.i18NextPipe.transform('page:' + m.label);
          title = this.i18NextPipe.transform('page:' + hit.label);
          break;
        }
      } else if (m.routerLink && this.matches(url, m.routerLink)) {
        crumb = this.i18NextPipe.transform('page:' + m.label);
        title = crumb;
        break;
      }
    }
    if (crumb) {
      this.crumb = crumb;
    }
    // Az oldalcímet az útvonal adja (breadcrumb-szolgáltatás); a menüből csak akkor, ha még nincs.
    if (title && !this.pageTitle) {
      this.pageTitle = title;
    }
    // Menün kívüli oldal (pl. Cégadatok): az útvonal data.title-je adja a címet.
    if (!this.pageTitle) {
      const routeTitle = this.routeDataTitle();
      if (routeTitle) {
        this.pageTitle = this.i18NextPipe.transform('page:' + routeTitle);
        this.crumb = this.crumb || this.pageTitle;
      }
    }
  }

  private routeDataTitle(): string | null {
    let snap = this.router.routerState.snapshot.root;
    let title: string | null = null;
    while (snap) {
      if (snap.data && snap.data['title']) {
        title = snap.data['title'];
      }
      snap = snap.firstChild;
    }
    return title;
  }

  private matches(url: string, routerLink: any[]): boolean {
    const target = routerLink.join('/').replace(/\/+/g, '/');
    if (target === '/' || target === '') {
      return url === '/' || url === '';
    }
    return url === target || url.startsWith(target + '/');
  }

  private resolveEnvLabel(): string {
    const env = (this.appInitService.currentConfig?.app?.env || '').toLowerCase();
    if (env === 'test') {
      return 'TESZT';
    }
    if (env === 'dev') {
      return 'DEV';
    }
    return '';
  }
}
